import { useState } from "react";
import { useContent } from "../../i18n";
import { formatNumber } from "./format";
import "./style.scss";

type Outcome = "approved" | "review" | "fraud" | "overLimit";

// Below this remaining balance after a claim, the organisation is warned the member is near their limit.
const NEAR_LIMIT = 1000;

export const ClaimDemo = () => {
  const { demos } = useContent();
  const t = demos.claim;

  const [amount, setAmount] = useState(1200);
  const [autoLimit, setAutoLimit] = useState(2000);
  const [remaining, setRemaining] = useState(8000);
  const [duplicate, setDuplicate] = useState(false);
  const [overPrice, setOverPrice] = useState(false);
  const [decision, setDecision] = useState<{ key: string; approve: boolean } | null>(null);

  const checks = {
    limit: amount <= remaining,
    auto: amount <= autoLimit,
    duplicate: !duplicate,
    price: !overPrice,
  };
  // A suspected duplicate outranks everything; exceeding the limit outranks a plain review.
  const outcome: Outcome = duplicate
    ? "fraud"
    : !checks.limit
      ? "overLimit"
      : !checks.auto || overPrice
        ? "review"
        : "approved";
  const after = remaining - amount;
  const nearLimit = (outcome === "approved" || outcome === "review") && after < NEAR_LIMIT;

  // A reviewer's decision belongs to one exact claim; changing anything clears it.
  const claimKey = [amount, autoLimit, remaining, duplicate, overPrice].join("|");
  const decided = decision?.key === claimKey ? decision : null;
  const money = (value: number) => `${formatNumber(value)} ${t.currency}`;

  return (
    <div className="demo">
      <div className="demo-stage claim-stage">
        <div className="claim-card">
          <p className="claim-member">{t.member}</p>
          <p className="claim-service">{t.service}</p>
          <p className="claim-provider">{t.provider}</p>
          <p className="claim-amount">{money(amount)}</p>
        </div>

        <div className="claim-checks">
          <p className="panel-title">{t.checksTitle}</p>
          <ul>
            {(Object.keys(checks) as (keyof typeof checks)[]).map((key) => (
              <li key={key} className={checks[key] ? "pass" : "fail"}>
                {t.checks[key]}
              </li>
            ))}
          </ul>
        </div>

        <div className={`claim-outcome ${decided ? (decided.approve ? "approved" : "fraud") : outcome}`}>
          <p>
            {decided ? (decided.approve ? t.decidedApprove : t.decidedReject) : t.outcomes[outcome]}
          </p>
          {(outcome === "approved" || outcome === "review") && (
            <p className="claim-after">
              {t.after}: <b>{money(after)}</b>
            </p>
          )}
          {nearLimit && <p className="claim-warn">{t.nearLimit}</p>}
          {outcome !== "approved" && !decided && (
            <div className="claim-review">
              <button type="button" className="act" onClick={() => setDecision({ key: claimKey, approve: true })}>
                {t.reviewerApprove}
              </button>
              <button type="button" className="quiet" onClick={() => setDecision({ key: claimKey, approve: false })}>
                {t.reviewerReject}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="demo-controls">
        <label className="range">
          <span>
            {t.amount}: <b>{money(amount)}</b>
          </span>
          <input type="range" min={100} max={10000} step={100} value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
        </label>
        <label className="range">
          <span>
            {t.autoLimit}: <b>{money(autoLimit)}</b>
          </span>
          <input type="range" min={0} max={5000} step={250} value={autoLimit} onChange={(e) => setAutoLimit(Number(e.target.value))} />
        </label>
        <label className="range">
          <span>
            {t.remaining}: <b>{money(remaining)}</b>
          </span>
          <input type="range" min={0} max={20000} step={500} value={remaining} onChange={(e) => setRemaining(Number(e.target.value))} />
        </label>

        <div className="switches">
          <label className="check">
            <input type="checkbox" checked={duplicate} onChange={(e) => setDuplicate(e.target.checked)} />
            <span>{t.duplicate}</span>
          </label>
          <label className="check">
            <input type="checkbox" checked={overPrice} onChange={(e) => setOverPrice(e.target.checked)} />
            <span>{t.overPrice}</span>
          </label>
        </div>

        <p className="demo-note">{demos.common.note}</p>
      </div>
    </div>
  );
};
